import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { ArrowDown, Document } from 'components/icons';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from 'components/Snackbar';
import { useTranslation } from 'hooks';
import {
  Paper,
  Form,
  SubmitButton,
  CancelButton,
  Text,
  FormFooter,
  ActionItem,
  DownloadLink,
  DownloadText,
  NDAContainer,
  NDAFooter,
  NDAText
} from './style';
import moment from 'moment';
import FormHelperText from '@mui/material/FormHelperText';
import AgreementHeader from 'components/AgreementHeader';
import { Header as BaseHeader } from 'components/cards/cards/Card/components';
import { styled } from '@mui/material/styles';

// FIX: Use MUI's styled instead of Emotion's styled to avoid conflicts
const Header = styled(BaseHeader)({});

function TermsPage({
  syncing = false,
  nda1 = {},
  nda2 = {},
  agreementData = {},
  language = 'en',
  onInit,
  onAccept,
  onAbort,
  setPolicyAccepted,
  canSkipAgreement = false,
  onSkipAgreement,
  isRegister = false
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [termsAcceptanceData, setTermsAcceptanceData] = useState({
    termsAccepted: false,
    privacyAccepted: false,
    expandedPanel: 'none'
  });
  const [panelExpanded, setPanelExpanded] = useState({ expanded: null });

  useEffect(() => {
    if (agreementData?.id && agreementData.isSuccess) {
      navigate('/dashboard', { state: { fromLogin: true } });
    }
  }, [agreementData, navigate]);

  useEffect(() => {
    onInit && onInit();
  }, [onInit]);

  useEffect(() => {
    if (isRegister) {
      const isTermsAccepted = JSON.parse(localStorage.getItem('termsAccepted')) || false;
      const isPrivacyAccepted = JSON.parse(localStorage.getItem('privacyAccepted')) || false;

      setTermsAcceptanceData(prev => ({
        ...prev,
        termsAccepted: isTermsAccepted,
        privacyAccepted: isPrivacyAccepted
      }));
    }
  }, [isRegister]);

  useEffect(() => {
    const { termsAccepted, privacyAccepted } = termsAcceptanceData;
    setPolicyAccepted && setPolicyAccepted(termsAccepted && privacyAccepted);
  }, [termsAcceptanceData.termsAccepted, termsAcceptanceData.privacyAccepted, setPolicyAccepted]);

  const handleChange = event => {
    const { name, checked } = event.target || {};
    setErrors(prev => ({ ...prev, [name]: null }));
    setTermsAcceptanceData(prev => ({
      ...prev,
      contractDate: moment().format('YYYY-MM-DD'),
      [name]: checked
    }));
    localStorage.setItem(name, checked);
  };

  const handleSubmit = () => {
    onAccept && onAccept({ ...termsAcceptanceData });
  };

  const handleAbort = () => {
    onAbort && onAbort();
    navigate('/login');
  };

  const handlePanelChange = (panel, event, expanded) => {
    if (!isRegister) {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
    setPanelExpanded({ expanded: expanded ? panel : false });
  };

  const handleSkip = () => {
    onSkipAgreement && onSkipAgreement();
    navigate('/dashboard');
  };

  // Safely extract text from nda1/nda2 objects
  const nda1Text = typeof nda1 === 'string' ? nda1 : nda1.text || '';
  const nda2Text = typeof nda2 === 'string' ? nda2 : nda2.text || '';

  return (
    <>
      {!isRegister && <AgreementHeader />}
      <Container style={isRegister ? { padding: '10px 0' } : {}}>
        <Snackbar
          message={errors.request}
          open={!!errors.request}
          variant="error"
          onClose={() => setErrors({ request: null })}
        />
        <Paper>
          <Accordion
            expanded={panelExpanded.expanded === 'terms'}
            id="terms"
            onChange={(e, expanded) => handlePanelChange('terms', e, expanded)}
            style={{ width: '100%' }}
          >
            <AccordionSummary
              aria-controls="panel1a-content"
              expandIcon={<ArrowDown />}
              id="panel1a-header"
            >
              <Header title={t('title.agreementTerms')} />
            </AccordionSummary>
            <AccordionDetails>
              <NDAContainer>
                <NDAText>{nda1Text}</NDAText>
                <NDAFooter $justify="space-between">
                  <ActionItem
                    as={DownloadLink}
                    color="primary"
                    component="a"
                    href={`/api/v1/manager/nda/agreement-file/${language}?timestamp=${new Date().getTime()}`}
                    target="_blank"
                  >
                    <Document />
                    <DownloadText>{t('link.downloadPDF')}</DownloadText>
                  </ActionItem>
                </NDAFooter>
              </NDAContainer>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={panelExpanded.expanded === 'privacy'}
            id="privacy"
            onChange={(e, expanded) => handlePanelChange('privacy', e, expanded)}
            style={{ width: '100%' }}
          >
            <AccordionSummary
              aria-controls="panel2a-content"
              expandIcon={<ArrowDown />}
              id="panel2a-header"
            >
              <Header title={t('title.dataProtection')} />
            </AccordionSummary>
            <AccordionDetails>
              <NDAContainer>
                <NDAText>{nda2Text}</NDAText>
                <NDAFooter $justify="space-between">
                  <ActionItem
                    as={DownloadLink}
                    color="primary"
                    component="a"
                    href={`/api/v1/manager/nda/protection-file/${language}?timestamp=${new Date().getTime()}`}
                    target="_blank"
                  >
                    <Document />
                    <DownloadText>{t('link.downloadPDF')}</DownloadText>
                  </ActionItem>
                </NDAFooter>
              </NDAContainer>
            </AccordionDetails>
          </Accordion>
          <Form noValidate>
            <FormFooter>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAcceptanceData.termsAccepted}
                    color="primary"
                    id="termsAccepted"
                    name="termsAccepted"
                    onChange={handleChange}
                  />
                }
                label={<Text>{t('label.acceptTerms')}</Text>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAcceptanceData.privacyAccepted}
                    color="primary"
                    id="privacyAccepted"
                    name="privacyAccepted"
                    onChange={handleChange}
                  />
                }
                label={<Text>{t('label.acceptPrivacy')}</Text>}
              />
            </FormFooter>
            <FormFooter>
              {!isRegister && (
                <>
                  {canSkipAgreement && (
                    <SubmitButton
                      color="primary"
                      disabled={syncing}
                      label={t('button.skip')}
                      size="large"
                      onClick={handleSkip}
                    />
                  )}
                  <CancelButton
                    color="primary"
                    disabled={syncing}
                    label={t('button.cancel')}
                    size="large"
                    onClick={handleAbort}
                  />
                  <SubmitButton
                    color="primary"
                    disabled={
                      syncing ||
                      !termsAcceptanceData.termsAccepted ||
                      !termsAcceptanceData.privacyAccepted
                    }
                    label={t('button.agreeRegisters')}
                    size="large"
                    onClick={handleSubmit}
                  />
                </>
              )}
            </FormFooter>
            <div style={{ float: 'right', minWidth: '26%' }}>
              {agreementData?.error && (
                <FormHelperText error>{agreementData.error.message}</FormHelperText>
              )}
            </div>
          </Form>
        </Paper>
      </Container>
    </>
  );
}

TermsPage.propTypes = {
  syncing: PropTypes.bool,
  nda1: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  nda2: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  agreementData: PropTypes.object,
  language: PropTypes.string,
  onInit: PropTypes.func,
  onAccept: PropTypes.func,
  onAbort: PropTypes.func,
  setPolicyAccepted: PropTypes.func,
  canSkipAgreement: PropTypes.bool,
  isRegister: PropTypes.bool,
  onSkipAgreement: PropTypes.func
};

export default TermsPage;
